import invoicesService from '@/services/invoices';
import expenseReceiptsService from '@/services/expenseReceipts';
import paymentReceiptsService from '@/services/paymentReceipts';
import simpleNotesService from '@/services/simpleNotes';
import {
  extractApiErrorMessage,
  extractPayload,
} from '@/utils/dataNormalization';

const mapRecipients = (source = []) => {
  return (Array.isArray(source) ? source : []).map((item) => ({
    id: item?.id,
    name: String(item?.name || '').trim(),
    email: String(item?.email || '').trim(),
    label: String(item?.label || '').trim(),
    isExternal: Boolean(item?.isExternal),
  }));
};

const buildBaseSendPayload = (submission = {}) => {
  const to = mapRecipients(submission?.to);
  const cc = mapRecipients(submission?.cc);

  return {
    to,
    cc,
    messageHtml: String(submission?.messageHtml || ''),
    messageText: String(submission?.messageText || ''),
  };
};

export const invoiceDetailSendEmailMethods = {
  openSendEmailModal() {
    this.showSendEmailModal = true;
  },
  closeSendEmailModal() {
    if (this.sendEmailModalSubmitting) {
      return;
    }

    this.showSendEmailModal = false;
  },
  buildSendInvoicePayload(submission = {}) {
    const payload = buildBaseSendPayload(submission);

    const invoiceAttachments = (Array.isArray(submission?.attachments?.invoices)
      ? submission.attachments.invoices
      : [])
      .map((item) => ({
        id: item?.id,
        label: String(item?.label || '').trim(),
        folio: String(item?.folio || '').trim(),
        date: String(item?.date || '').trim(),
        totalLabel: String(item?.totalLabel || '').trim(),
        uuid: String(item?.uuid || '').trim(),
      }));

    const documentExisting = (Array.isArray(submission?.attachments?.documents?.existing)
      ? submission.attachments.documents.existing
      : [])
      .map((item) => ({
        id: item?.id,
        document_id: Number(item?.document_id || item?.id || 0) || 0,
        label: String(item?.label || '').trim(),
      }));

    const documentNew = (Array.isArray(submission?.attachments?.documents?.new)
      ? submission.attachments.documents.new
      : [])
      .map((item) => ({
        id: item?.id,
        label: String(item?.label || '').trim(),
        fileName: String(item?.file?.name || item?.label || '').trim(),
      }));

    return {
      ...payload,
      attachments: {
        invoices: invoiceAttachments,
        documents: {
          existing: documentExisting,
          new: documentNew,
        },
      },
    };
  },
  buildSendPaymentReceiptPayload(submission = {}) {
    const payload = buildBaseSendPayload(submission);

    const documentExisting = (Array.isArray(submission?.attachments?.documents?.existing)
      ? submission.attachments.documents.existing
      : [])
      .map((item) => ({
        id: item?.id,
        document_id: Number(item?.document_id || item?.id || 0) || 0,
        label: String(item?.label || '').trim(),
      }));

    const documentNew = (Array.isArray(submission?.attachments?.documents?.new)
      ? submission.attachments.documents.new
      : [])
      .map((item) => ({
        id: item?.id,
        label: String(item?.label || '').trim(),
        fileName: String(item?.file?.name || item?.label || '').trim(),
      }));

    return {
      ...payload,
      attachments: {
        documents: {
          existing: documentExisting,
          new: documentNew,
        },
      },
    };
  },
  buildSendExpenseReceiptPayload(submission = {}) {
    return this.buildSendPaymentReceiptPayload(submission);
  },
  buildSendSimpleNotePayload(submission = {}) {
    const payload = buildBaseSendPayload(submission);

    const documentExisting = (Array.isArray(submission?.attachments?.documents?.existing)
      ? submission.attachments.documents.existing
      : [])
      .map((item) => ({
        id: item?.id,
        document_id: Number(item?.document_id || item?.id || 0) || 0,
        label: String(item?.label || '').trim(),
      }));

    const documentNew = (Array.isArray(submission?.attachments?.documents?.new)
      ? submission.attachments.documents.new
      : [])
      .map((item) => ({
        id: item?.id,
        label: String(item?.label || '').trim(),
        fileName: String(item?.file?.name || item?.label || '').trim(),
      }));

    return {
      ...payload,
      attachments: {
        documents: {
          existing: documentExisting,
          new: documentNew,
        },
      },
    };
  },
  async submitSendEmailModal(submission = {}) {
    if (this.sendEmailModalSubmitting) {
      return;
    }

    this.sendEmailModalSubmitting = true;

    try {
      const sendType = String(submission?.type || '').trim();

      switch (sendType) {
      case 'invoice': {
        const invoiceId = Number(this.invoiceId || 0) || 0;
        if (invoiceId <= 0) {
          throw new Error('invoice_id_missing');
        }

        const payload = this.buildSendInvoicePayload(submission);
        const response = await invoicesService.sendInvoice(invoiceId, payload);

        const apiError = extractApiErrorMessage(response, '');
        if (apiError !== '') {
          this.$toast?.error(apiError);
          return;
        }

        const toastMessage = String(
          this.sendEmailText?.invoices?.toasts?.submitted || ''
        ).trim();

        if (toastMessage !== '') {
          if (typeof this.$toast?.success === 'function') {
            this.$toast.success(toastMessage);
          } else {
            this.$toast?.info?.(toastMessage);
          }
        }

        const responsePayload = extractPayload(response);
        const sentAt = String(responsePayload?.shipping?.sent_at || responsePayload?.sent_at || '').trim();

        if (this.invoiceData && typeof this.invoiceData === 'object') {
          this.invoiceData = {
            ...this.invoiceData,
            has_received_proof: 1,
            last_sent_at: sentAt !== '' ? sentAt : String(this.invoiceData?.last_sent_at || '').trim(),
            sent_at: sentAt !== '' ? sentAt : String(this.invoiceData?.sent_at || '').trim(),
          };
        }

        this.invoiceShippingRefreshTick = Number(this.invoiceShippingRefreshTick || 0) + 1;
        this.showSendEmailModal = false;
        break;
      }
      case 'payment_receipt': {
        const paymentReceiptId = Number(this.paymentReceiptId || 0) || 0;
        if (paymentReceiptId <= 0) {
          throw new Error('payment_receipt_id_missing');
        }

        const payload = this.buildSendPaymentReceiptPayload(submission);
        const response = await paymentReceiptsService.sendPaymentReceipt(paymentReceiptId, payload);

        const apiError = extractApiErrorMessage(response, '');
        if (apiError !== '') {
          this.$toast?.error(apiError);
          return;
        }

        const toastMessage = String(
          this.sendEmailText?.paymentReceipts?.toasts?.submitted || ''
        ).trim();

        if (toastMessage !== '') {
          if (typeof this.$toast?.success === 'function') {
            this.$toast.success(toastMessage);
          } else {
            this.$toast?.info?.(toastMessage);
          }
        }

        const responsePayload = extractPayload(response);
        const sentAt = String(responsePayload?.shipping?.sent_at || responsePayload?.sent_at || '').trim();

        if (this.paymentReceiptData && typeof this.paymentReceiptData === 'object') {
          this.paymentReceiptData = {
            ...this.paymentReceiptData,
            sent: true,
            has_received_proof: 1,
            last_sent_at: sentAt !== '' ? sentAt : String(this.paymentReceiptData?.last_sent_at || '').trim(),
            sent_at: sentAt !== '' ? sentAt : String(this.paymentReceiptData?.sent_at || '').trim(),
          };
        }

        this.showSendEmailModal = false;
        break;
      }
      case 'expense_receipt': {
        const expenseReceiptId = Number(this.expenseReceiptId || 0) || 0;
        if (expenseReceiptId <= 0) {
          throw new Error('expense_receipt_id_missing');
        }

        const payload = this.buildSendExpenseReceiptPayload(submission);
        const response = await expenseReceiptsService.sendExpenseReceipt(expenseReceiptId, payload);

        const apiError = extractApiErrorMessage(response, '');
        if (apiError !== '') {
          this.$toast?.error(apiError);
          return;
        }

        const toastMessage = String(
          this.sendEmailText?.expenseReceipts?.toasts?.submitted
          || this.sendEmailText?.paymentReceipts?.toasts?.submitted
          || ''
        ).trim();

        if (toastMessage !== '') {
          if (typeof this.$toast?.success === 'function') {
            this.$toast.success(toastMessage);
          } else {
            this.$toast?.info?.(toastMessage);
          }
        }

        const responsePayload = extractPayload(response);
        const sentAt = String(responsePayload?.shipping?.sent_at || responsePayload?.sent_at || '').trim();

        if (this.expenseReceiptData && typeof this.expenseReceiptData === 'object') {
          this.expenseReceiptData = {
            ...this.expenseReceiptData,
            sent: true,
            has_received_proof: 1,
            last_sent_at: sentAt !== '' ? sentAt : String(this.expenseReceiptData?.last_sent_at || '').trim(),
            sent_at: sentAt !== '' ? sentAt : String(this.expenseReceiptData?.sent_at || '').trim(),
          };
        }

        this.showSendEmailModal = false;
        break;
      }
      case 'simple_note': {
        const simpleNoteId = Number(this.noteId || this.simpleNoteId || 0) || 0;
        if (simpleNoteId <= 0) {
          throw new Error('simple_note_id_missing');
        }

        const payload = this.buildSendSimpleNotePayload(submission);
        const response = await simpleNotesService.sendSimpleNote(simpleNoteId, payload);

        const apiError = extractApiErrorMessage(response, '');
        if (apiError !== '') {
          this.$toast?.error(apiError);
          return;
        }

        const toastMessage = String(
          this.sendEmailText?.simpleNotes?.toasts?.submitted || ''
        ).trim();

        if (toastMessage !== '') {
          if (typeof this.$toast?.success === 'function') {
            this.$toast.success(toastMessage);
          } else {
            this.$toast?.info?.(toastMessage);
          }
        }

        const responsePayload = extractPayload(response);
        const sentAt = String(responsePayload?.shipping?.sent_at || responsePayload?.sent_at || '').trim();

        if (this.note && typeof this.note === 'object') {
          this.note = {
            ...this.note,
            sent: true,
            has_received_proof: 1,
            last_sent_at: sentAt !== '' ? sentAt : String(this.note?.last_sent_at || '').trim(),
            sent_at: sentAt !== '' ? sentAt : String(this.note?.sent_at || '').trim(),
          };
        }

        this.noteShippingRefreshTick = Number(this.noteShippingRefreshTick || 0) + 1;
        this.showSendEmailModal = false;
        break;
      }
      default:
        throw new Error('send_email_type_not_supported');
      }
    } catch (error) {
      const sendType = String(submission?.type || '').trim();
      const fallback = sendType === 'payment_receipt'
        ? String(this.sendEmailText?.paymentReceipts?.toasts?.failed || '').trim()
        : (sendType === 'expense_receipt'
          ? String(this.sendEmailText?.expenseReceipts?.toasts?.failed || '').trim()
          : (sendType === 'simple_note'
            ? String(this.sendEmailText?.simpleNotes?.toasts?.failed || '').trim()
            : String(this.sendEmailText?.invoices?.toasts?.failed || '').trim()));
      const message = extractApiErrorMessage(error?.response || error, fallback);

      if (message !== '') {
        this.$toast?.error(message);
      }
    } finally {
      this.sendEmailModalSubmitting = false;
    }
  },
};
